import React, { Component } from 'react';
import styled from 'styled-components';
import { Colors } from 'modules/theme';
import { push } from 'modules/history';
import SmartSearchBar, { chipsToFilters } from 'components/SmartSearchBar';
import H4 from 'components/H4';
import ButtonText from 'components/ButtonText';
import PaginationButtons from 'components/PaginationButtons';
import Table, { THeader, TableTitle, TRow, TableItem, TBody } from 'components/Table';
import Filter from 'components/Filter';
import AlertConfirm from 'components/AlertConfirm';
import ListEmpty from 'components/ListEmpty';
import Loader from 'components/Loader';
import Icon from 'components/Icon';
import { Tooltip } from 'react-tooltip';
import PropTypes from 'prop-types';
import moment from 'moment';
import { KineticoRolesCode } from 'constants/index';
import {
  listHeaderPrimaryButtonStyles,
  listHeaderSecondaryButtonStyles,
} from 'constants/listHeaderButtons';

const REGULAR_ALERT_LOOKBACK_DAYS = 2;

const Container = styled.div`
  padding: 24px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  padding-bottom: 16px;
  gap: 12px;
  min-height: 44px;
`;

const SearchContainer = styled.div`
  flex: 1 1 260px;
  min-width: 220px;
  max-width: 440px;
  display: flex;
  align-items: center;
  @media (max-width: 768px) {
    flex-basis: 100%;
    max-width: 100%;
  }
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  @media (max-width: 768px) {
    flex-basis: 100%;
  }
`;

const SummaryBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 0 4px 14px;
`;

const SummaryLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: ${Colors.gray};
`;

const ResultCount = styled.span`
  font-weight: 600;
  color: ${Colors.black2};
`;

const InlineSpinner = styled.span`
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid ${Colors.gray2};
  border-top-color: ${Colors.lightBlue};
  border-radius: 50%;
  animation: ssb-spin 0.6s linear infinite;
  @keyframes ssb-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ClearAll = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${Colors.lightBlue};
  font-size: 13px;
  font-weight: 500;
  padding: 0;
  :hover {
    text-decoration: underline;
  }
`;

const Mark = styled.mark`
  background: rgba(20, 152, 203, 0.18);
  color: inherit;
  font-weight: 600;
  padding: 0 1px;
  border-radius: 2px;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  margin-left: auto;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TableContainer = styled.div`
  padding-bottom: 50px;
  margin-bottom: 50px;
`;

const Footer = styled.div`
  position: fixed;
  bottom: 0;
  left: 250px;
  right: 0;
  padding: 0 24px 25px 24px;
  background-color: ${Colors.white};
  box-sizing: border-box;
  @media (max-width: 700px) {
    left: 60px;
  }
`;

// Wrapper for the status icon to center it properly within the table cell
const StatusIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

// A colored circle container for status icons
// Green for normal operation, yellow for alerts, gray for not installed.
const StatusIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${props => {
    if (!props.installed) return Colors.gray;
    return props.hasAlerts ? Colors.yellow : Colors.green;
  }};
`;

const DisconnectedIconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DisconnectedSlash = styled.span`
  position: absolute;
  width: 15px;
  border-top: 2px solid ${Colors.white};
  transform: rotate(-35deg);
`;

const QuickActionsDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const QuickActionButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 33px;
  justify-content: center;
  padding: 0;
  width: 33px;

  &:hover {
    background: ${Colors.gray2};
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: white;
  min-width: 190px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 10;
  border-radius: 4px;
  overflow: hidden;
  display: ${props => (props.isOpen ? 'block' : 'none')};
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  text-decoration: none;
  display: flex;
  align-items: center;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  line-height: 17px;
  font-weight: 350;

  &:hover {
    background-color: #f1f1f1;
  }
`;

const DisabledDropdownItem = styled(DropdownItem)`
  color: ${Colors.gray3};
  cursor: not-allowed;

  &:hover {
    background-color: transparent;
  }
`;

export default class LocationListView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dropdownOpenId: null,
      locationSelected: null,
      isOpenModal: false,
      isOpenAlert: false,
      chips: [],
      search: '',
      brand_id: props.initialBrandId || '',
      account_id: props.initialAccountId || '',
      state: '',
      installed: '',
      city_id: '',
      contact: '',
    };
  }

  componentDidUpdate(prevProps) {
    // If initialBrandId or initialAccountId change, update state
    if (
      prevProps.initialBrandId !== this.props.initialBrandId ||
      prevProps.initialAccountId !== this.props.initialAccountId
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        brand_id: this.props.initialBrandId || '',
        account_id: this.props.initialAccountId || '',
      });
    }
  }

  componentWillUnmount() {
    if (this.fetchTimer) {
      clearTimeout(this.fetchTimer);
    }
  }

  // Build the location-list query params from the current chip + free-text state.
  currentFilters = page => {
    const { search, brand_id, account_id, state, installed, city_id, contact } = this.state;
    const params = { search, brand_id, account_id, state, installed, city_id, contact };
    if (page) {
      params.page = page;
    }
    return params;
  };

  fetchNow = () => {
    if (this.fetchTimer) {
      clearTimeout(this.fetchTimer);
    }
    this.props.fetchLocations(this.currentFilters(1));
  };

  // Debounced so typing free text in the bar doesn't fire a request per keystroke.
  fetchDebounced = () => {
    if (this.fetchTimer) {
      clearTimeout(this.fetchTimer);
    }
    this.fetchTimer = setTimeout(() => {
      this.props.fetchLocations(this.currentFilters(1));
    }, 400);
  };

  // Chips are the single source of truth; dropdown mirrors are derived from them
  // so the smart bar and the dropdowns always stay in sync.
  applyChips = (chips, search, { debounceFetch = false } = {}) => {
    const filters = chipsToFilters(chips);
    this.setState(
      {
        chips,
        search,
        brand_id: filters.brand_id != null ? filters.brand_id : '',
        account_id: filters.account_id != null ? filters.account_id : '',
        state: filters.state != null ? filters.state : '',
        installed: filters.installed != null ? filters.installed : '',
        city_id: filters.city_id != null ? filters.city_id : '',
        contact: filters.contact != null ? filters.contact : '',
      },
      () => {
        if (debounceFetch) {
          this.fetchDebounced();
        } else {
          this.fetchNow();
        }
      },
    );
  };

  // Smart search bar -> table. Tighten results by dropping account chips when the
  // brand changes, and keep the account dropdown options scoped to the new brand.
  handleSmartChange = ({ chips, text }) => {
    const prevChips = this.state.chips;
    const chipsChanged =
      prevChips.length !== chips.length ||
      prevChips.some((chip, index) => {
        const next = chips[index];
        return !next || chip.type !== next.type || chip.id !== next.id || chip.value !== next.value;
      });

    const prevBrand = chipsToFilters(prevChips).brand_id;
    let nextChips = chips;
    const nextBrand = chipsToFilters(nextChips).brand_id;
    const brandChanged = String(prevBrand || '') !== String(nextBrand || '');

    if (brandChanged) {
      nextChips = nextChips.filter(chip => chip.type !== 'account');
    }

    this.applyChips(nextChips, text, { debounceFetch: !chipsChanged });

    if (brandChanged) {
      const finalBrand = chipsToFilters(nextChips).brand_id;
      this.props.fetchAccounts(finalBrand ? { brands: [finalBrand] } : {});
    }
  };

  labelForOption = (options, value) => {
    const found = (options || []).find(option => String(option.value) === String(value));
    return found ? found.label : value;
  };

  // Dropdown -> chips. Each dropdown maps to a single-value chip type.
  filterOnChange = event => {
    const { name, value } = event.target;
    const { brands, accounts, states } = this.props;
    let chips = this.state.chips.filter(chip => {
      if (name === 'brand_id') return chip.type !== 'brand' && chip.type !== 'account';
      if (name === 'account_id') return chip.type !== 'account';
      if (name === 'state') return chip.type !== 'state';
      if (name === 'installed') return chip.type !== 'installed';
      return true;
    });

    if (name === 'brand_id') {
      if (value) {
        chips = chips.concat({
          type: 'brand',
          id: value,
          label: this.labelForOption(brands, value),
        });
      }
      this.applyChips(chips, this.state.search);
      this.props.fetchAccounts(value ? { brands: [value] } : {});
      return;
    }

    if (name === 'account_id' && value) {
      chips = chips.concat({
        type: 'account',
        id: value,
        label: this.labelForOption(accounts, value),
      });
    } else if (name === 'state' && value) {
      chips = chips.concat({ type: 'state', id: value, label: this.labelForOption(states, value) });
    } else if (name === 'installed' && value !== '') {
      chips = chips.concat({
        type: 'installed',
        value,
        label: value === 'true' || value === true ? 'Installed' : 'Not Installed',
      });
    }

    this.applyChips(chips, this.state.search);
  };

  clearAllFilters = () => {
    this.setState(
      {
        chips: [],
        search: '',
        brand_id: '',
        account_id: '',
        state: '',
        installed: '',
        city_id: '',
        contact: '',
      },
      () => {
        this.props.fetchAccounts({});
        this.fetchNow();
      },
    );
  };

  renderHighlight = value => {
    const text = value == null ? '' : String(value);
    const query = (this.state.search || '').trim();
    if (!query || !text) {
      return text;
    }
    const safe = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${safe})`, 'ig'));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? <Mark key={index}>{part}</Mark> : part,
    );
  };

  switchDropdown = key => {
    const { dropdownOpenId } = this.state;
    const id = dropdownOpenId !== key ? key : null;

    this.setState({ dropdownOpenId: id });
  };

  switchModal = () => {
    const { isOpenModal } = this.state;

    this.setState({ isOpenModal: !isOpenModal });
  };

  switchAlert = (locationSelected = null) => {
    const { isOpenAlert } = this.state;

    this.setState({ isOpenAlert: !isOpenAlert, locationSelected });
  };

  editLocation = ({ id }) => {
    push(`/locations/${id}/edit`);
  };

  viewLocation = ({ id }) => {
    push(`/locations/${id}`);
  };

  getHealthCheckDeviceType = location => {
    if (location.ReverseOsmosisFilterHead) {
      return 'reverse-osmosis';
    }
    if (location.WaterFilterHead) {
      return 'water-filter';
    }
    if (location.SoftenerFilterHead) {
      return 'water-softener';
    }

    return null;
  };

  runHealthCheck = location => {
    const { showAlert, startHealthCheck, startHealthCheckLoading } = this.props;

    if (startHealthCheckLoading) {
      return;
    }

    if (!location.installed) {
      return;
    }

    const deviceType = this.getHealthCheckDeviceType(location);

    if (!deviceType) {
      if (showAlert) {
        showAlert('No device found for this location', {
          variant: 'warning',
          timeout: 9,
        });
      }
      return;
    }

    startHealthCheck({
      location_id: location.id,
      device_type: deviceType,
    });
    this.setState({ dropdownOpenId: null });
  };

  remove = () => {
    const { removeLocation } = this.props;
    const {
      locationSelected: { id },
    } = this.state;
    removeLocation(id);
    this.switchAlert();
  };

  // Format the alert timestamp to a user-friendly format
  // Displays as MM/DD/YYYY at h:mma (ex: 03/10/2025 at 4:40am)
  formatAlertDate = dateString => {
    const date = moment(dateString);
    const formattedDate = date.format('MM/DD/YYYY');
    const formattedTime = date.format('h:mma').toLowerCase();
    return `${formattedDate} at ${formattedTime}`;
  };

  getLatestAlert = alerts =>
    alerts.reduce((latestAlert, currentAlert) => {
      if (!latestAlert) return currentAlert;
      return moment(currentAlert.created_at).isAfter(moment(latestAlert.created_at))
        ? currentAlert
        : latestAlert;
    }, null);

  isDisconnectAlert = alert => {
    const trigger = alert && alert.AlertType ? alert.AlertType.trigger : '';
    const title = alert && alert.title ? alert.title.toLowerCase() : '';

    return (
      trigger === 'connection-lost' ||
      title.includes('connection lost') ||
      title.includes('disconnected') ||
      title.includes('disconnect')
    );
  };

  // Plain text tooltip content - fallback for no alerts
  renderAlertTooltipContent = alerts => {
    if (!alerts || alerts.length === 0) {
      return 'Operating normally';
    }

    const alert = this.getLatestAlert(alerts);

    return `Trigger Date: ${this.formatAlertDate(alert.created_at)}\n${alert.title}`;
  };

  // HTML tooltip content for better formatting of alert info
  // Shows trigger date and alert title in a more readable format
  renderAlertTooltipHTML = alerts => {
    if (!alerts || alerts.length === 0) {
      return 'Operating normally';
    }

    const alert = this.getLatestAlert(alerts);

    return `<strong>Trigger Date:</strong> ${this.formatAlertDate(alert.created_at)}<br /><strong>${
      alert.title
    }</strong>`;
  };

  renderItem = (item, key) => {
    const { role_code, startHealthCheckLoading } = this.props;
    // Non-disconnect alerts still drive the yellow warning icon.
    const hasAlerts = item.Alerts && item.Alerts.length > 0;
    const tooltipId = `location-status-${item.id}`;
    const regularWindowStart = moment().subtract(REGULAR_ALERT_LOOKBACK_DAYS, 'days');

    // Live connectivity from API (telemetry older than 30 minutes).
    const isDisconnected = item.is_disconnected === true;

    const hasRecentAlerts =
      hasAlerts &&
      item.Alerts.some(alert => {
        const alertDate = moment(alert.created_at);
        return (
          alertDate.isValid() &&
          alertDate.isSameOrAfter(regularWindowStart) &&
          !this.isDisconnectAlert(alert)
        );
      });

    // Get installation status
    const isInstalled = !!item.installed;

    // Determine tooltip content based on status
    let tooltipContent = 'Not installed';
    if (isInstalled) {
      if (isDisconnected) {
        tooltipContent = 'This location is currently disconnected';
      } else if (hasRecentAlerts) {
        tooltipContent = this.renderAlertTooltipContent(item.Alerts);
      } else {
        tooltipContent = 'Operating normally';
      }
    }

    return (
      <TRow key={key}>
        <TableItem
          width="15%"
          style={{ cursor: 'pointer', color: Colors.lightBlue }}
          onClick={() => this.viewLocation(item)}
        >
          {this.renderHighlight(item.identificator)}
        </TableItem>
        <TableItem width="10%">
          {item.store_number ? this.renderHighlight(item.store_number) : '-'}
        </TableItem>
        <TableItem width="15%">{this.renderHighlight(item.brand_name)}</TableItem>
        <TableItem width="15%">{this.renderHighlight(item.account_name)}</TableItem>
        <TableItem width="15%">{this.renderHighlight(item.full_address)}</TableItem>
        <TableItem width="13%">
          {item.contact_email || item.LocationsPerUsers?.find(lpu => lpu.User)?.User?.email || '-'}
        </TableItem>
        {/* Status column: live connectivity + recent non-disconnect alerts */}
        <TableItem width="10%" isComponent style={{ justifyContent: 'center' }}>
          <StatusIconWrapper>
            <StatusIcon
              installed={isInstalled}
              hasAlerts={isDisconnected || hasRecentAlerts}
              data-tooltip-id={tooltipId}
              data-tooltip-html={
                isDisconnected
                  ? tooltipContent
                  : hasRecentAlerts
                  ? this.renderAlertTooltipHTML(item.Alerts)
                  : tooltipContent
              }
            >
              {!isInstalled ? (
                <Icon name="times" fontSize={12} color={Colors.white} />
              ) : isDisconnected ? (
                <DisconnectedIconWrapper>
                  <Icon name="wifi" fontSize={10} color={Colors.white} />
                  <DisconnectedSlash />
                </DisconnectedIconWrapper>
              ) : hasRecentAlerts ? (
                <Icon name="exclamation" fontSize={12} color={Colors.white} />
              ) : (
                <Icon name="check" fontSize={12} color={Colors.white} />
              )}
            </StatusIcon>
            <Tooltip id={tooltipId} />
          </StatusIconWrapper>
        </TableItem>
        <TableItem width="7%" isComponent>
          <QuickActionsDropdown>
            <QuickActionButton type="button" onClick={() => this.switchDropdown(key)}>
              <Icon name="ellipsis-v" color={Colors.black} fontSize={14} />
            </QuickActionButton>
            <DropdownContent isOpen={this.state.dropdownOpenId === key}>
              <DropdownItem onClick={() => this.viewLocation(item)}>
                <Icon
                  name="eye"
                  color={Colors.gray}
                  fontSize={14}
                  customStyle={{ marginRight: '8px' }}
                />
                View
              </DropdownItem>

              {KineticoRolesCode.includes(role_code) && (
                <>
                  <DropdownItem onClick={() => this.editLocation(item)}>
                    <Icon
                      name="pencil-alt"
                      color={Colors.gray}
                      fontSize={14}
                      customStyle={{ marginRight: '8px' }}
                    />
                    Edit
                  </DropdownItem>

                  {item.installed ? (
                    <DropdownItem onClick={() => this.runHealthCheck(item)}>
                      <Icon
                        name="heartbeat"
                        color={Colors.gray}
                        fontSize={14}
                        customStyle={{ marginRight: '8px' }}
                      />
                      {startHealthCheckLoading ? 'Running Health Check' : 'Run Health Check'}
                    </DropdownItem>
                  ) : (
                    <DisabledDropdownItem>
                      <Icon
                        name="heartbeat"
                        color={Colors.gray3}
                        fontSize={14}
                        customStyle={{ marginRight: '8px' }}
                      />
                      Run Health Check
                    </DisabledDropdownItem>
                  )}

                  <DropdownItem onClick={() => this.switchAlert(item)}>
                    <Icon
                      name="trash"
                      color={Colors.gray}
                      fontSize={14}
                      customStyle={{ marginRight: '8px' }}
                    />
                    Remove
                  </DropdownItem>
                </>
              )}
            </DropdownContent>
          </QuickActionsDropdown>
        </TableItem>
      </TRow>
    );
  };

  renderAlertConfirm = () => {
    const { isOpenAlert, locationSelected } = this.state;

    return (
      <AlertConfirm
        title="Remove location"
        onCancel={() => this.switchAlert()}
        onSuccess={this.remove}
        successText="Remove"
        isVisible={isOpenAlert}
      >
        <H4 style={{ textAlign: 'center' }}>
          {`Are you sure that can remove the "${
            locationSelected ? locationSelected.identificator : ''
          }" location`}
        </H4>
      </AlertConfirm>
    );
  };

  render() {
    const {
      list,
      pages,
      currentPage,
      count,
      loading,
      states,
      brands,
      accounts,
      fetchLocations,
      role_code,
    } = this.props;
    const { chips, search, brand_id, account_id, state, installed } = this.state;
    const hasActiveFilters = chips.length > 0 || !!(search && search.trim());
    const resultCount = typeof count === 'number' ? count : list.length;

    return (
      <Container>
        <Header>
          <SearchContainer>
            <SmartSearchBar
              chips={chips}
              text={search}
              onChange={this.handleSmartChange}
              placeholder="Search brand, account, location, store #, address…"
            />
          </SearchContainer>
          <FilterBar>
            <Filter
              compact
              title="Brand"
              selectedValue={brand_id}
              name="brand_id"
              handleChange={this.filterOnChange}
              options={brands}
            />
            <Filter
              compact
              title="Account"
              selectedValue={account_id}
              name="account_id"
              handleChange={this.filterOnChange}
              options={accounts}
            />
            <Filter
              compact
              title="State"
              selectedValue={state}
              name="state"
              handleChange={this.filterOnChange}
              options={states}
            />
            <Filter
              compact
              title="Installation"
              selectedValue={installed}
              name="installed"
              handleChange={this.filterOnChange}
              options={[
                { value: '', label: 'All Installations' },
                { value: true, label: 'Installed' },
                { value: false, label: 'Not Installed' },
              ]}
            />
          </FilterBar>
          {KineticoRolesCode.includes(role_code) && (
            <ButtonContainer>
              <ButtonGroup>
                <ButtonText
                  theme="basic"
                  text="Import Approval"
                  icon="file-import"
                  customStyles={listHeaderSecondaryButtonStyles}
                  onClick={() => push('/locations/import-approval')}
                />
                <ButtonText
                  theme="primary"
                  text="New"
                  icon="plus"
                  customStyles={listHeaderPrimaryButtonStyles}
                  onClick={() => push('/locations/create')}
                />
              </ButtonGroup>
            </ButtonContainer>
          )}
        </Header>
        <SummaryBar>
          <SummaryLeft>
            {loading && <InlineSpinner />}
            <span>
              <ResultCount>{resultCount.toLocaleString()}</ResultCount>{' '}
              {resultCount === 1 ? 'location' : 'locations'}
              {hasActiveFilters ? ' match your search' : ''}
            </span>
          </SummaryLeft>
          {hasActiveFilters && <ClearAll onClick={this.clearAllFilters}>Clear all</ClearAll>}
        </SummaryBar>
        <TableContainer>
          <Table>
            <THeader>
              <TableTitle width="15%">Location ID</TableTitle>
              <TableTitle width="10%">Store #</TableTitle>
              <TableTitle width="15%">Brand</TableTitle>
              <TableTitle width="15%">Account</TableTitle>
              <TableTitle width="15%">Address</TableTitle>
              <TableTitle width="13%">Contact</TableTitle>
              <TableTitle width="10%">Status</TableTitle>
              <TableTitle width="7%" />
            </THeader>
            <TBody>
              {loading ? (
                <Loader size={100} block />
              ) : (
                list.map((item, key) => this.renderItem(item, key))
              )}
            </TBody>
          </Table>
          {(!list || !list.length) && !loading && (
            <ListEmpty
              message={
                hasActiveFilters
                  ? 'No locations match your search. Try removing a filter.'
                  : 'There are currently no locations'
              }
            />
          )}
        </TableContainer>
        <Footer>
          <PaginationButtons
            currentPage={currentPage}
            changePage={page => fetchLocations(this.currentFilters(page))}
            totalPages={pages}
          />
        </Footer>
        {this.renderAlertConfirm()}
      </Container>
    );
  }
}

LocationListView.propTypes = {
  accounts: PropTypes.array,
  brands: PropTypes.array,
  count: PropTypes.number,
  currentPage: PropTypes.number,
  fetchAccounts: PropTypes.func,
  fetchLocations: PropTypes.func,
  initialAccountId: PropTypes.string,
  initialBrandId: PropTypes.string,
  list: PropTypes.array,
  loading: PropTypes.bool,
  pages: PropTypes.number,
  removeLocation: PropTypes.func,
  role_code: PropTypes.string,
  showAlert: PropTypes.func,
  startHealthCheck: PropTypes.func,
  startHealthCheckLoading: PropTypes.bool,
  states: PropTypes.array,
};

LocationListView.defaultProps = {
  initialBrandId: '',
  initialAccountId: '',
};
